import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, ListChecks, BookOpen, Sparkles } from "lucide-react";

const COVER = {
  covered: { Icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  partial: { Icon: AlertTriangle, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  missing: { Icon: XCircle, cls: "text-rose-600 bg-rose-50 border-rose-200" },
};

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

// Single consolidated card: Knowledge Graph coverage + ONE "Strengthen your
// answer" block (merges missed authorities/examples, keywords, data) so gaps
// are shown in exactly one place. `gaps` = { examples, keywords, data }.
export default function IntelligencePanel({ intelligence, gaps }) {
  if (!intelligence) return null;
  const { coverage_pct, dimensions = [], facts = {}, cost } = intelligence;
  const citedLabel = intelligence.facts_label || "Authorities you cited";
  const matched = facts.matched || [];

  const missedExamples = uniq([...(facts.missing || []), ...((gaps && gaps.examples) || [])]);
  const keywords = uniq((gaps && gaps.keywords) || []);
  const data = uniq((gaps && gaps.data) || []);
  const hasGaps = missedExamples.length || keywords.length || data.length;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="intelligence-panel">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="w-4 h-4 text-blue-700" />
        <h4 className="font-semibold text-slate-800 text-sm">Knowledge Graph Coverage</h4>
        <span className="ml-auto text-xs font-bold text-blue-700" data-testid="intel-coverage">{coverage_pct}%</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {dimensions.map((d, i) => {
          const s = COVER[d.coverage] || COVER.missing;
          const Ic = s.Icon;
          return (
            <span key={i} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${s.cls}`}>
              <Ic className="w-3.5 h-3.5" />{d.label}
            </span>
          );
        })}
      </div>

      {matched.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" />{citedLabel}
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="intel-facts-matched">
            {matched.map((f, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{f}</span>
            ))}
          </div>
        </div>
      )}

      {hasGaps ? (
        <div className="rounded-lg border border-rose-100 bg-white/70 p-3" data-testid="strengthen-block">
          <p className="text-xs font-semibold text-rose-700 mb-2 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />Strengthen your answer
          </p>
          <div className="space-y-2.5">
            {missedExamples.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Examples &amp; authorities</p>
                <div className="flex flex-wrap gap-1.5" data-testid="strengthen-examples">
                  {missedExamples.map((f, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded border border-rose-200 text-rose-700">{f}</span>
                  ))}
                </div>
              </div>
            )}
            {keywords.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Keywords</p>
                <div className="flex flex-wrap gap-1.5" data-testid="strengthen-keywords">
                  {keywords.map((k, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-600">{k}</span>
                  ))}
                </div>
              </div>
            )}
            {data.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Data &amp; reports</p>
                <ul className="space-y-1" data-testid="strengthen-data">
                  {data.map((d, i) => (
                    <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-rose-400">•</span>{d}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {cost && (
        <p className="text-[10px] text-slate-400 mt-3">
          Graded against the syllabus knowledge graph · {(cost.processing_ms / 1000).toFixed(1)}s
        </p>
      )}
    </div>
  );
}
